import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent } from '../ui/card';
import { Clock, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogSection = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/blog/posts?limit=4`);
      setPosts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      setLoading(false);
      // Fallback to mock data if API fails
      setPosts([
        {
          id: '1',
          title: { en: 'Dubai Real Estate Market Trends 2026', ar: 'اتجاهات سوق العقارات في دبي 2026' },
          excerpt: { en: 'An in-depth analysis of the latest trends shaping Dubai\'s commercial real estate sector', ar: 'تحليل متعمق لأحدث الاتجاهات التي تشكل قطاع العقارات التجارية في دبي' },
          category: { en: 'Market Analysis', ar: 'تحليل السوق' },
          image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800',
          readTime: '8 min read',
          likes: 245
        }
      ]);
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-[#C5A038]/20 text-[#12532c] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            {t({ en: 'Blog', ar: 'المدونة' })}
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t({ en: 'Guides & Articles for Investors', ar: 'أدلة ومقالات للمستثمرين' })}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t({
              en: 'Everything you need to know about living and investing in Dubai',
              ar: 'كل ما تحتاج لمعرفته عن العيش والاستثمار في دبي'
            })}
          </p>
        </div>

        {/* Blog Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">{t({ en: 'Loading articles...', ar: 'جاري تحميل المقالات...' })}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {posts.map((post) => (
              <Card
                key={post.id}
                className="group overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer border-0"
                onClick={() => navigate('/blog')}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={post.image || 'https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?w=800&q=75&auto=format'}
                    alt={typeof post.title === 'object' ? t(post.title) : post.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4">
                    <div className="bg-white/90 backdrop-blur-sm text-gray-900 px-3 py-1 rounded-full text-xs font-semibold">
                      {typeof post.category === 'object' ? t(post.category) : post.category}
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-[#12532c] transition-colors">
                    {typeof post.title === 'object' ? t(post.title) : post.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {typeof post.excerpt === 'object' ? t(post.excerpt) : post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      <span>{post.read_time || post.readTime || '5 min read'}</span>
                    </div>
                    <div className="flex items-center">
                      <Heart className="w-4 h-4 mr-1" />
                      <span>{post.likes || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* View All Button */}
        <div className="text-center">
          <Button
            size="lg"
            onClick={() => navigate('/blog')}
            variant="outline"
            className="border-2 border-gray-900 hover:bg-gray-900 hover:text-white"
          >
            {t({ en: 'View All Articles', ar: 'عرض جميع المقالات' })}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
